

// Datei: src/shop_crit.public.ajax.js
export class AjaxError extends Error {
    constructor(message, url, status) {
        var path = '';
        try { path = new URL(url, location.origin).pathname; } catch {}
        super(path ? message + ' — ' + path : message);
        this.name = 'AjaxError';
        this.url = url;
        this.status = status;
    }
}

export class ProgressBar {
    #element = null;
    #rafId = null;
    #startTime = null;
    #duration = 20000;

    start() {
        this.#element = document.getElementById("progress") ?? this.#createElement();
        this.#startTime = performance.now();
        this.#animate();
    }

    #createElement() {
        const el = document.createElement("div");
        el.id = "progress";
        document.body.appendChild(el);
        return el;
    }

    #animate() {
        const elapsed = performance.now() - this.#startTime;
        const progress = Math.min((elapsed / this.#duration) * 90, 90);
        this.#element.style.width = `${progress}%`;
        if (progress < 90) {
            this.#rafId = requestAnimationFrame(() => this.#animate());
        }
    }

    async stop() {
        if (this.#rafId) cancelAnimationFrame(this.#rafId);
        const el = this.#element;
        if (!el) return;
        
        this.#element = null;
        this.#rafId = null;

        el.style.cssText = 'width:100%;transition:width .3s ease-out';
        await new Promise(r => setTimeout(r, 300));

        el.style.cssText = 'opacity:0;transition:opacity .3s ease-out';
        await new Promise(r => setTimeout(r, 300));

        el.remove();
    }
}

export class AjaxService {
    // Statische Konstanten - einmal definiert, nie neu erstellt
    static #ALLOWED_STATUS = new Set([200, 201, 202, 204, 404]);
    static #DEFAULT_HEADERS = {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-Requested-With": "XMLHttpRequest"
    };

    #pendingRequests = new Map();
    #pendingTimestamps = new Map();
    #ajaxCache = new Map();
    #cleanupInterval = null;
    
    // Konstanten als Instanz-Properties (schnellerer Zugriff als statische)
    #CACHE_DURATION = 300000;
    #CLEANUP_INTERVAL = 300000;
    #PENDING_TIMEOUT = 120000;
    #MAX_CACHE_SIZE = 100;
    #DEFAULT_TIMEOUT = 30000;
    #MAX_RETRIES = 3;
    #RETRY_DELAY = 500;

    #prepareData(data) {
        if (!data) return '';
        if (data instanceof FormData || typeof data === 'string') return data;
        return new URLSearchParams(data).toString();
    }

    #handlePreloadLinks(response) {
        const linkHeader = response.headers.get("Link");
        if (!linkHeader) return;
        
        // Alte Preload-Links entfernen
        domCache.getAll('link[rel="preload"]').forEach(l => l.remove());
        
        const fragment = document.createDocumentFragment();
        const { hostname } = location;
        const regex = /<(.+)>;\s+rel=preload;\s+as=(\w+)/;
        
        for (const link of linkHeader.split(",")) {
            const match = link.match(regex);
            if (!match) continue;
            
            const preload = document.createElement("link");
            preload.rel = "preload";
            preload.href = match[1];
            preload.as = match[2];
            
            if (match[1].startsWith("https://") && !match[1].includes(hostname)) {
                preload.crossOrigin = "anonymous";
            }
            
            fragment.appendChild(preload);
        }
        
        document.head.appendChild(fragment);
    }

    async #fetchWithRetry(url, opts, retries, delay, signal) {
        let lastError;
        
        for (let i = 0; i < retries; i++) {
            try {
                const res = await fetch(url, { ...opts, signal });
                if (AjaxService.#ALLOWED_STATUS.has(res.status)) return res;
                lastError = new AjaxError(`HTTP ${res.status}`, url, res.status);
            } catch (err) {
                if (err.name === 'AbortError') throw err;
                lastError = err;
            }
            
            if (i < retries - 1) {
                await new Promise(r => setTimeout(r, Math.min(delay << i, 5000)));
            }
        }
        throw lastError ?? new Error(`Failed after ${retries} retries`);
    }

    #startCleanupIfNeeded() {
        if (this.#cleanupInterval) return;
        
        this.#cleanupInterval = setInterval(() => {
            const now = Date.now();
            
            // Cache cleanup
            for (const [key, value] of this.#ajaxCache) {
                if (now - value.timestamp > this.#CACHE_DURATION) {
                    this.#ajaxCache.delete(key);
                }
            }
            
            // Pending cleanup
            for (const [key, timestamp] of this.#pendingTimestamps) {
                if (now - timestamp > this.#PENDING_TIMEOUT) {
                    this.#pendingRequests.delete(key);
                    this.#pendingTimestamps.delete(key);
                }
            }
            
            // Stop wenn leer
            if (!this.#ajaxCache.size && !this.#pendingRequests.size) {
                clearInterval(this.#cleanupInterval);
                this.#cleanupInterval = null;
            }
        }, this.#CLEANUP_INTERVAL);
    }

    request(options) {
        const {
            url,
            type = 'POST',
            data = null,
            dataType = 'html',
            cache = false,
            maxRetries = this.#MAX_RETRIES,
            retryDelay = this.#RETRY_DELAY,
            progressBar = false,
            timeout = this.#DEFAULT_TIMEOUT
        } = options;
        
        if (!url) throw new Error("URL ist undefiniert");

        const method = type.toUpperCase();
        const cacheKey = `${method}:${url}:${data ? JSON.stringify(data) : ''}`;
        
        // Pending check
        const pending = this.#pendingRequests.get(cacheKey);
        if (pending) return pending;

        // Cache check
        if (cache) {
            const cached = this.#ajaxCache.get(cacheKey);
            if (cached && Date.now() - cached.timestamp < this.#CACHE_DURATION) {
                cached.lastAccess = Date.now();
                return Promise.resolve(cached.data);
            }
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        let progress = null;
        if (progressBar) {
            progress = new ProgressBar();
            progress.start();
        }

        let requestUrl = url;
        const requestOptions = {
            method,
            headers: AjaxService.#DEFAULT_HEADERS,
            cache: cache ? 'default' : 'no-store',
            credentials: 'include'
        };

        if (method === 'GET' && data) {
            requestUrl += (url.includes('?') ? '&' : '?') + this.#prepareData(data);
        } else if (data) {
            requestOptions.body = this.#prepareData(data);
        }

        const promise = this.#fetchWithRetry(requestUrl, requestOptions, maxRetries, retryDelay, controller.signal)
            .then(async res => {
                clearTimeout(timeoutId);
                progress?.stop();
                this.#handlePreloadLinks(res);
                
                const text = await res.text();
                
                if (dataType === 'json') {
                    try {
                        return JSON.parse(text);
                    } catch {
                        throw new AjaxError('JSON Parse Error', url, res.status);
                    }
                }
                return text;
            })
            .then(result => {
                if (cache) {
                    const now = Date.now();
                    this.#ajaxCache.set(cacheKey, { 
                        data: result, 
                        timestamp: now, 
                        lastAccess: now 
                    });
                    
                    // LRU Eviction
                    if (this.#ajaxCache.size > this.#MAX_CACHE_SIZE) {
                        let oldestKey = null;
                        let oldestTime = Infinity;
                        
                        for (const [key, value] of this.#ajaxCache) {
                            if (value.lastAccess < oldestTime) {
                                oldestTime = value.lastAccess;
                                oldestKey = key;
                            }
                        }
                        
                        if (oldestKey) this.#ajaxCache.delete(oldestKey);
                    }
                }
                
                this.#pendingRequests.delete(cacheKey);
                this.#pendingTimestamps.delete(cacheKey);
                return result;
            })
            .catch(err => {
                clearTimeout(timeoutId);
                progress?.stop();
                this.#pendingRequests.delete(cacheKey);
                this.#pendingTimestamps.delete(cacheKey);
                
                if (err.name === 'AbortError') {
                    throw new AjaxError(`Timeout nach ${timeout}ms`, url, 0);
                }
                throw err instanceof AjaxError ? err : new AjaxError(err.message, url, 0);
            });

        this.#pendingRequests.set(cacheKey, promise);
        this.#pendingTimestamps.set(cacheKey, Date.now());
        this.#startCleanupIfNeeded();
        
        return promise;
    }
}